import { Component, HostBinding, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgForm } from '@angular/forms';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { Endpoint, HelpIndex } from '../../../../models/enums';
import { PasswordCrackingSettings, PasswordCrackingSettingsViewModel } from '../../../../models/password-cracking-settings';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { Template } from '../../../../models/template';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { Permissions, ObjectActionListType, ActionType } from '../../../../models/permission';
import { FoundPasswordsModalComponent } from '../../found-passwords-modal/found-passwords-modal.component';
import { KnowFrequentlyUsedPasswordsModalComponent } from '../../known-frequently-used-passwords-modal/known-frequently-used-passwords-modal.component';

enum Dictionary {
    KNOWN_PASSWORD_LIST = 'KNOWN_PASSWORD_LIST',
    PASSWORD_DICTIONARY = 'PASSWORD_DICTIONARY'
}

@Component({
    standalone: false,
    selector: 'res-password-cracking-settings',
    templateUrl: './password-cracking-settings.component.html',
    styleUrls: ['./password-cracking-settings.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class PasswordCrackingSettingsComponent implements SettingsNavigationContentComponent {
    readonly Dictionary = Dictionary;
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('contentForm', { static: true }) contentForm: NgForm;
    @ViewChild('errorPop', { static: true }) errorPop: PopoverDirective;

    isBusy = false;
    isBusyMessage: string;
    validChanges = true;
    helpIndex: HelpIndex;
    handle: string;
    knownPasswordListBytes: any;
    passwordDictionaryBytes: any;
    endpoint: Endpoint;
    viewModel: PasswordCrackingSettingsViewModel = new PasswordCrackingSettingsViewModel(new PasswordCrackingSettings());
    foundPasswordsChecked = true;
    knownPassUpdateDateUser = '';
    freqUsedPassUpdateDateUser = '';

    // permissions
    hasViewOnlyPermissions: boolean;

    get isDirty(): boolean {
        if (this.contentForm && this.viewModel.isDirty()) {
            return this.contentForm.dirty;
        }
        return false;
    }
    set isDirty(value: boolean) {
        if (this.contentForm && this.contentForm.control) {
            if (value) {
                this.contentForm.control.markAsDirty();
            } else {
                this.contentForm.control.markAsPristine();
            }
        }
    }

    constructor(private commonService: CommonService,
        private restService: RestService) { }

    loadSettingsData(handle: string): void {
        this.errorPop.hide();
        this.isBusyMessage = 'Loading Password Cracking Settings...';
        this.isBusy = true;
        this.restService.post(Endpoint.projectManager_getPasswordSettings, { handle: handle }).then(response => {
            if (response) {
                this.viewModel = new PasswordCrackingSettingsViewModel(response);
                this.knownPassUpdateDateUser = this.viewModel.model.knownPassswordDictionaryUpdateDate ? this.commonService.insertYesterday(this.viewModel.model.knownPassswordDictionaryUpdateDate) +
                    (this.viewModel.model.knownPassswordDictionaryUpdatedBy ? ' by ' + this.viewModel.model.knownPassswordDictionaryUpdatedBy : '') : '';
                this.freqUsedPassUpdateDateUser = this.viewModel.model.frequentlyUsedPassswordDictionaryUpdateDate ? this.commonService.insertYesterday(this.viewModel.model.frequentlyUsedPassswordDictionaryUpdateDate) +
                    (this.viewModel.model.frequentlyUsedPassswordDictionaryUpdatedBy ? ' by ' + this.viewModel.model.frequentlyUsedPassswordDictionaryUpdatedBy : '') : '';
            }
            this.isBusy = false;
            this.isDirty = false;
        }, error => {
            this.isBusy = false;
        });
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string): void {
        this.handle = node.data instanceof Template ? node.data.handle : projectHandle;
        this.helpIndex = HelpIndex.case_security_password_file_dlg;
        this.hasViewOnlyPermissions = !Permissions.checkUserPermission(ObjectActionListType.PRJ_PASSWORD_CRACKING, ActionType.CREATE);
        this.loadSettingsData(this.handle);
    }

    openHelp = (): void => this.commonService.openHelp(this.helpIndex);

    saveChanges(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if (this.handle) {
                const passwordCrackingSettings = new PasswordCrackingSettings(this.viewModel.model);
                passwordCrackingSettings.handle = this.handle;

                passwordCrackingSettings.bruteMinChars = this.viewModel.bruteMinChars;
                passwordCrackingSettings.bruteMaxChars = this.viewModel.bruteMaxChars;

                delete passwordCrackingSettings['status'];
                delete passwordCrackingSettings['errorCode'];
                delete passwordCrackingSettings['auditLogUrl'];
                delete passwordCrackingSettings['extendedStatus'];
                delete passwordCrackingSettings['numberResults'];
                delete passwordCrackingSettings['sessionToken'];
                delete passwordCrackingSettings['warningDetails'];
                delete passwordCrackingSettings['passwordCrackingDictionaryName'];
                delete passwordCrackingSettings['passwordCrackingDictionarySize'];
                delete passwordCrackingSettings['passwordCrackingDictionaryUploadDate'];
                delete passwordCrackingSettings['passwordCrackingDictionaryUploadUser'];
                delete passwordCrackingSettings['passwordGoldenDictionaryName'];
                delete passwordCrackingSettings['passwordGoldenDictionarySize'];
                delete passwordCrackingSettings['passwordGoldenDictionaryUploadDate'];
                delete passwordCrackingSettings['passwordGoldenDictionaryUploadUser'];
                delete passwordCrackingSettings['passwordGoldenDictionaryUpdateDate'];
                delete passwordCrackingSettings['passwordGoldenDictionaryUpdatedBy'];
                delete passwordCrackingSettings['knownPassswordDictionaryUpdateDate'];
                delete passwordCrackingSettings['knownPassswordDictionaryUpdatedBy'];
                delete passwordCrackingSettings['knownPassswordCount'];
                delete passwordCrackingSettings['frequentlyUsedPasswordCount'];
                delete passwordCrackingSettings['frequentlyUsedPassswordDictionaryUpdateDate'];
                delete passwordCrackingSettings['frequentlyUsedPassswordDictionaryUpdatedBy'];
                delete passwordCrackingSettings['foundPasswordCount'];

                this.isBusyMessage = 'Saving Password Cracking Settings...';
                this.isBusy = true;
                this.restService.post(Endpoint.projectManager_setPasswordSettings, passwordCrackingSettings).then(response => {
                    this.isBusy = false;
                    this.isDirty = false;
                    this.loadSettingsData(this.handle);
                    resolve(true);
                }, error => {
                    this.isBusy = false;
                    resolve(false);
                });
            } else {
                resolve(false);
            }
        });
    }

    discardChanges(): void {
        this.isDirty = false;
        this.loadSettingsData(this.handle);
    }

    validatePasswordLength(): void {
        if (this.viewModel.bruteMinChars > this.viewModel.bruteMaxChars) {
            this.errorPop.show();
        } else {
            this.errorPop.hide();
        }
    }

    isDisabled(): boolean {
        let isDisabled = true;
        if (!this.isBusy && this.isDirty && (this.viewModel.bruteMinChars <= this.viewModel.bruteMaxChars)) {
            isDisabled = false;
        }
        return isDisabled;
    }

    isDiscardDisabled(): boolean {
        let isDisabled = true;
        if (!this.isBusy && this.isDirty) {
            isDisabled = false;
        }
        return isDisabled;
    }

    onChange(value?: string): void {
        this.isDirty = this.viewModel.isDirty();
    }

    viewFoundPasswords(): void {
        this.commonService.openModal(FoundPasswordsModalComponent, { class: 'vertical-percent-80', modalData: { handle: this.handle } });
    }

    openKnownFrequentlyUsedPasswords(type): void {
        this.commonService.openModal(KnowFrequentlyUsedPasswordsModalComponent, {
            class: 'vertical-percent-80',
            modalData: {
                modalType: type,
                handle: this.handle
            }
        }).then(response => {
            if (response?.modalData) {
                this.restService.post(Endpoint.projectManager_getPasswordSettings, { handle: this.handle }).then(response => {
                    if (response) {
                        this.viewModel.model.knownPassswordCount = response.knownPassswordCount;
                        this.viewModel.model.frequentlyUsedPasswordCount = response.frequentlyUsedPasswordCount;
                        this.knownPassUpdateDateUser = response.knownPassswordDictionaryUpdateDate ? this.commonService.insertYesterday(response.knownPassswordDictionaryUpdateDate) +
                            (response.knownPassswordDictionaryUpdatedBy ? ' by ' + response.knownPassswordDictionaryUpdatedBy : '') : '';
                        this.freqUsedPassUpdateDateUser = response.frequentlyUsedPassswordDictionaryUpdateDate ? this.commonService.insertYesterday(response.frequentlyUsedPassswordDictionaryUpdateDate) +
                            (response.frequentlyUsedPassswordDictionaryUpdatedBy ? ' by ' + response.frequentlyUsedPassswordDictionaryUpdatedBy : '') : '';
                    }
                });
            }
        });
    }

    setBruteForceDefaultOptions(): void {
        this.viewModel.model.bruteLowerCase = this.viewModel.model.bruteUpperCase = this.viewModel.model.bruteDigits = this.viewModel.model.bruteForce;
    }
}
